import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface PriceHistoryData {
  id: number;
  price: number;
  created_at: string;
}

interface Props {
  productId: number;
}

export default function PriceHistory({ productId }: Props) {
  const [history, setHistory] = useState<PriceHistoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchPriceHistory();
  }, [productId, period]);

  const fetchPriceHistory = async () => {
    try {
      setLoading(true);
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('price_history')
        .select('*')
        .eq('product_id', productId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Fiyat geçmişi yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMinPrice = () => {
    if (history.length === 0) return 0;
    return Math.min(...history.map(h => h.price));
  };

  const getMaxPrice = () => {
    if (history.length === 0) return 0;
    return Math.max(...history.map(h => h.price));
  };

  const getPriceChange = () => {
    if (history.length < 2) return 0;
    const firstPrice = history[0].price;
    const lastPrice = history[history.length - 1].price;
    return ((lastPrice - firstPrice) / firstPrice) * 100;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const normalizePrice = (price: number) => {
    const min = getMinPrice();
    const max = getMaxPrice();
    if (max === min) return 50;
    return ((price - min) / (max - min)) * 100;
  };

  const priceChange = getPriceChange();

  return (
    <div className="mb-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Fiyat Geçmişi</h2>
        <div className="flex items-center space-x-2">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                period === p
                  ? 'bg-rose-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-rose-500'
              }`}
            >
              {p === '7d' ? '7 Gün' : p === '30d' ? '30 Gün' : '90 Gün'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-12 border border-gray-200 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : history.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-gray-200 text-center">
          <i className="ri-line-chart-line text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-600">Bu ürün için henüz fiyat geçmişi bulunmuyor</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">En Düşük Fiyat</p>
              <p className="text-2xl font-bold text-emerald-600">{getMinPrice().toFixed(2)} ₺</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">En Yüksek Fiyat</p>
              <p className="text-2xl font-bold text-rose-600">{getMaxPrice().toFixed(2)} ₺</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Değişim</p>
              <p className={`text-2xl font-bold ${priceChange >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className="relative h-64">
            <svg className="w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map((y) => (
                <line
                  key={y}
                  x1="0"
                  y1={200 - (y * 2)}
                  x2="800"
                  y2={200 - (y * 2)}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              ))}

              {/* Price line */}
              <polyline
                points={history
                  .map((h, i) => {
                    const x = (i / (history.length - 1)) * 800;
                    const y = 200 - normalizePrice(h.price) * 2;
                    return `${x},${y}`;
                  })
                  .join(' ')}
                stroke="#f43f5e"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Area fill */}
              <polygon
                points={`0,200 ${history
                  .map((h, i) => {
                    const x = (i / (history.length - 1)) * 800;
                    const y = 200 - normalizePrice(h.price) * 2;
                    return `${x},${y}`;
                  })
                  .join(' ')} 800,200`}
                fill="url(#gradient)"
                opacity="0.2"
              />

              {/* Gradient definition */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Data points */}
              {history.map((h, i) => {
                const x = (i / (history.length - 1)) * 800;
                const y = 200 - normalizePrice(h.price) * 2;
                return (
                  <g key={h.id}>
                    <circle cx={x} cy={y} r="4" fill="#f43f5e" />
                    <circle cx={x} cy={y} r="8" fill="#f43f5e" opacity="0.2" />
                  </g>
                );
              })}
            </svg>

            {/* X-axis labels */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2 text-xs text-gray-500">
              <span>{formatDate(history[0].created_at)}</span>
              {history.length > 2 && (
                <span>{formatDate(history[Math.floor(history.length / 2)].created_at)}</span>
              )}
              <span>{formatDate(history[history.length - 1].created_at)}</span>
            </div>
          </div>

          {/* Price list */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fiyat Değişimleri</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {history.slice().reverse().map((h, i) => {
                const prevPrice = i < history.length - 1 ? history[history.length - 2 - i].price : h.price;
                const change = h.price - prevPrice;
                return (
                  <div key={h.id} className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">
                      {new Date(h.created_at).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <div className="flex items-center space-x-3">
                      <span className="text-lg font-semibold text-gray-900">{h.price.toFixed(2)} ₺</span>
                      {change !== 0 && (
                        <span className={`text-sm font-medium ${change > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {change > 0 ? '+' : ''}{change.toFixed(2)} ₺
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
